import { TaskResponse, UpdateTaskRequest } from "@9amhealth/openapi";
import { getLocalTimeZone, parseAbsolute } from "@internationalized/date";
import { Cubit } from "blac-next";
import { PTHW_BINARY_RATING_QUESTIONNAIRE_ID } from "src/constants/misc";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import { taskManagementState, toast, userState } from "src/state/state";
import { TaskResponseKnown } from "src/state/TaskManagementBloc/TaskManagementBloc";
import { useAppQueryPopupOpenMethod } from "../AppQueryPopups/AppQueryPopupsBloc";
import {
  UserJourneyLesson,
  UserJourneyLessonItem,
  UserJourneyModule
} from "./UserJourneyBloc";

/**
 * State for JourneyModuleTriggerBloc
 */
type JourneyModuleTriggerState = {
  isSkipSheetOpen: boolean;
};

/**
 * Props for JourneyModuleTriggerBloc
 */
export type JourneyModuleTriggerProps = {
  item: UserJourneyLessonItem;
  lesson: UserJourneyLesson;
  module: UserJourneyModule;
  onTaskStatusUpdated: (status: string) => void;
  questionnaireTasks: TaskResponseKnown[];
  enableAll: boolean;
  popups: {
    openTypeformPopup: useAppQueryPopupOpenMethod;
    closeTypeformPopup: () => void;
    openModuleRatingPopup: useAppQueryPopupOpenMethod;
    closeModuleRatingPopup: () => void;
    openAppointmentDetailsPopup: useAppQueryPopupOpenMethod;
    openSchedulerPopup: useAppQueryPopupOpenMethod;
  };
};

/**
 * Result object for starting a task
 */
type StartTaskResult = {
  triggerButtonAnimation: boolean;
};

/**
 * Bloc for handling business logic of JourneyModuleTrigger
 *
 * Handles task actions, state transitions, and popup management for journey modules.
 * Implements Cubit pattern for state management.
 */
export class JourneyModuleTriggerBloc extends Cubit<
  JourneyModuleTriggerState,
  JourneyModuleTriggerProps
> {
  //#region Fields
  /**
   * Whether the rating has been answered for this item
   */
  isRatingAnswered: boolean = false;

  /**
   * Ref to the trigger button element
   */
  refButton: React.RefObject<HTMLButtonElement | null> = { current: null };
  //#endregion

  //#region Constructor & Initialization
  /**
   * Constructs the JourneyModuleTriggerBloc
   * @param props - Bloc props
   */
  constructor(props: JourneyModuleTriggerProps) {
    super({
      isSkipSheetOpen: false
    });
    this.props = props;
  }

  /**
   * Initializes the bloc, auto-triggers task if needed, and checks rating status
   */
  init = () => {
    this.autoClickOnTask();
    void this.checkIfRatingAnswered();
  };
  //#endregion

  //#region Getters
  /**
   * Returns the current item, throws an error if not defined
   */
  get item() {
    if (!this.props?.item) {
      throw new Error("item is not defined");
    }
    return this.props.item;
  }

  /**
   * Returns the current task, throws if item is not defined
   */
  get task() {
    return this.item.task;
  }

  /**
   * Returns the appointment ID if available
   */
  get appointmentId() {
    return this.task?.additionalData?.appointment_id as string;
  }

  /**
   * Returns true if the item is a questionnaire
   */
  get isQuestionnaire() {
    return this.item.type === "typeform";
  }

  /**
   * Returns true if the item is a coaching task
   */
  get isCoaching() {
    return this.item.type === "coaching";
  }

  /**
   * Returns true if the task is completed
   */
  get isCompleted() {
    return this.task?.status === TaskResponse.status.COMPLETED;
  }

  /**
   * Returns true if the task is skipped
   */
  get isSkipped() {
    return this.task?.status === TaskResponse.status.SKIPPED;
  }

  /**
   * Returns true if the task is in progress
   */
  get isInProgress() {
    return this.task?.status === TaskResponse.status.IN_PROGRESS;
  }

  /**
   * Returns true if the task is available
   */
  get isAvailable() {
    return this.task?.status === TaskResponse.status.AVAILABLE;
  }

  /**
   * Returns the index of the questionnaire in the list, returns -1 if not found
   */
  get questionnaireIndex() {
    if (!this.props?.questionnaireTasks) {
      return -1;
    }
    return this.props.questionnaireTasks.findIndex((q) => q === this.task);
  }

  /**
   * Returns the current lesson, throws if not defined
   */
  get lesson() {
    if (!this.props?.lesson) {
      throw new Error("lesson is not defined");
    }
    return this.props.lesson;
  }

  /**
   * Returns the current module, throws if not defined
   */
  get module() {
    if (!this.props?.module) {
      throw new Error("module is not defined");
    }
    return this.props.module;
  }

  /**
   * Returns the scheduling URL if available
   */
  get schedulingUrl() {
    return this.task?.additionalData?.["scheduling_url"] as string | undefined;
  }

  /**
   * Returns true if coaching info is available
   */
  get coachingInfoAvailable() {
    return Boolean(this.isCoaching && this.isInProgress && this.schedulingUrl);
  }

  /**
   * Returns the module starting date as a parsed date object
   */
  get moduleStartingDate() {
    if (!this.task?.availableFrom) {
      return undefined;
    }
    return parseAbsolute(this.task.availableFrom, getLocalTimeZone());
  }
  //#endregion

  //#region Ref Management
  /**
   * Sets the ref for the trigger button and auto-scrolls to it if needed
   * @param ref - Ref object for the button
   */
  setRefButton = (ref: React.RefObject<HTMLButtonElement | null>) => {
    this.refButton = ref;
    this.autoScrollToButton();
  };
  //#endregion

  //#region Task & UI Actions
  /**
   * Automatically triggers the task action if URL params match
   */
  autoClickOnTask = () => {
    const { task } = this;
    const urlParams = new URLSearchParams(window.location.search);
    const groupParam = urlParams.get("group");
    const slugParam = urlParams.get("slug");

    if (task?.group === groupParam && task.slug === slugParam) {
      this.triggerTaskAction();
    }
  };

  /**
   * Scrolls to the trigger button if the task is available or in progress
   */
  autoScrollToButton = () => {
    const status = this.task?.status;
    const allowedStatuses = [
      TaskResponse.status.AVAILABLE,
      TaskResponse.status.IN_PROGRESS
    ];

    if (status && allowedStatuses.includes(status) && this.refButton.current) {
      this.refButton.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  };
  //#endregion

  //#region Rating & Questionnaire
  /**
   * Checks if the rating has been answered for this item
   */
  checkIfRatingAnswered = async (): Promise<void> => {
    if (this.item.typeformId) {
      const answers = await userState.getBinaryRatingQuestionnaireAnswers(
        this.item.typeformId
      );
      this.isRatingAnswered = answers.length > 0;
    }
  };

  /**
   * Starts the module rating popup if not already answered
   */
  startRatingPopup = () => {
    if (this.isRatingAnswered || !this.item.typeformId) {
      return;
    }
    this.props?.popups.openModuleRatingPopup({
      additionalParameters: {
        preventLoadingUserAnswers: "true",
        title: this.lesson.name,
        questionnaireId: PTHW_BINARY_RATING_QUESTIONNAIRE_ID,
        rating_subject_uid: this.item.typeformId
      },
      onEvent: {
        nineQuestionnaireSaved: () => {
          this.props?.popups.closeModuleRatingPopup();
        }
      }
    });
  };
  //#endregion

  //#region Task Status Management
  /**
   * Sets the status of the current task
   * @param status - New status to set
   */
  setTaskStatus = (status: string) => {
    void taskManagementState.updateTaskStatus(
      {
        program: KnownProgram.LIFEBALANCE,
        group: this.props?.lesson.taskGroup,
        slug: this.task?.slug
      },
      status as UpdateTaskRequest.status
    );
    this.props?.onTaskStatusUpdated(status);
  };
  //#endregion

  //#region Task Starters
  /**
   * Starts a questionnaire task, opening the typeform popup and handling events
   * @returns StartTaskResult
   */
  startQuestionnaireTask = (): StartTaskResult => {
    let triggerButtonAnimation = false;
    const { task } = this;
    if (!task) {
      reportErrorSentry(new Error("Task is not defined"));
      toast.error("error.generic.short.tryAgain");
      return { triggerButtonAnimation };
    }
    if (!this.item.typeformId) {
      reportErrorSentry(new Error("Typeform ID is required"));
      toast.error("error.generic.short.tryAgain");
      return { triggerButtonAnimation };
    }

    const statusHasAction = [
      TaskResponse.status.AVAILABLE,
      TaskResponse.status.IN_PROGRESS,
      TaskResponse.status.COMPLETED,
      TaskResponse.status.SKIPPED
    ].includes(task.status);

    if (this.props?.enableAll || statusHasAction) {
      triggerButtonAnimation = true;
      this.props?.popups.openTypeformPopup({
        additionalParameters: {
          stay: "false",
          questionnaireId: this.item.typeformId,
          title: this.lesson.name
        },
        onEvent: {
          nineQuestionnaireStarted: () => {
            if (task.status === TaskResponse.status.AVAILABLE) {
              this.setTaskStatus(TaskResponse.status.IN_PROGRESS);
            }
          },
          nineQuestionnaireSaved: () => {
            this.setTaskStatus(TaskResponse.status.COMPLETED);
            this.props?.popups.closeTypeformPopup();
            this.startRatingPopup();
          }
        }
      });
    }
    return { triggerButtonAnimation };
  };

  /**
   * Starts a coaching task, handling skip sheet and browser opening
   * @returns StartTaskResult
   */
  startCoachingTask = (): StartTaskResult => {
    let triggerButtonAnimation = false;
    const url = this.schedulingUrl;
    if (!url) {
      reportErrorSentry(new Error("Coaching URL is required"));
      toast.error("error.generic.short.tryAgain");
      return { triggerButtonAnimation };
    }
    const canSkip = this.task?.additionalData?.canBeSkippedByUser === true;
    if (
      this.props?.enableAll ||
      (this.task?.status === TaskResponse.status.AVAILABLE && canSkip)
    ) {
      triggerButtonAnimation = true;
      this.patch({ isSkipSheetOpen: true });
    } else if (
      this.task?.status === TaskResponse.status.AVAILABLE ||
      (this.task?.status === TaskResponse.status.IN_PROGRESS &&
        this.appointmentId)
    ) {
      if (
        this.task.status === TaskResponse.status.IN_PROGRESS &&
        this.appointmentId
      ) {
        this.props?.popups.openAppointmentDetailsPopup({
          additionalParameters: {
            id: this.appointmentId
          }
        });
      } else {
        triggerButtonAnimation = true;
        this.props?.popups.openSchedulerPopup({
          additionalParameters: {
            url: encodeURIComponent(url),
            title: translate("scheduleCoachingSession", {
              lessonName: this.lesson.name
            })
          }
        });
      }
    }
    return { triggerButtonAnimation };
  };
  //#endregion

  //#region Main Task Trigger
  /**
   * Triggers the appropriate action for the current task (questionnaire or coaching)
   */
  triggerTaskAction = () => {
    if (!this.props?.popups) {
      throw new Error("popups are not defined");
    }
    const { task } = this;
    if (!task) {
      reportErrorSentry(new Error("Lesson item task is not defined"));
      toast.error("error.generic.short.tryAgain");
      return;
    }
    let startTaskResult: StartTaskResult = { triggerButtonAnimation: false };
    const blockedStatuses: TaskResponse.status[] = [TaskResponse.status.LOCKED];
    const statusHasAction = !blockedStatuses.includes(task.status);
    if (!statusHasAction && !this.props.enableAll) {
      return;
    }
    if (this.isQuestionnaire) {
      startTaskResult = this.startQuestionnaireTask();
    }
    if (this.isCoaching) {
      startTaskResult = this.startCoachingTask();
    }
    if (startTaskResult.triggerButtonAnimation && this.refButton.current) {
      this.refButton.current.removeAttribute("transition");
      requestAnimationFrame(() => {
        this.refButton.current?.setAttribute(
          "transition",
          this.task?.status ?? "none"
        );
      });
    }
  };
  //#endregion

  //#region Sheet Handlers
  /**
   * Handles closing the skip sheet
   */
  handleSheetClose = () => {
    this.patch({ isSkipSheetOpen: false });
  };

  /**
   * Handles skipping the task from the skip sheet
   */
  handleSheetSkip = () => {
    this.setTaskStatus(TaskResponse.status.SKIPPED);
    this.patch({ isSkipSheetOpen: false });
  };

  /**
   * Handles continuing from the skip sheet, opening the scheduling URL
   */
  handleSheetContinue = () => {
    const url = this.task?.additionalData?.scheduling_url as string;
    if (url) {
      this.props?.popups.openSchedulerPopup({
        additionalParameters: {
          url: encodeURIComponent(url),
          title: translate("scheduleCoachingSession", {
            lessonName: this.lesson.name
          })
        }
      });
    }
    this.patch({ isSkipSheetOpen: false });
  };
  //#endregion
}
